import type { ThemeTokens } from './types';

// v2 theme recipe: a small, serializable input from which deterministic
// `ThemeTokens` and a 6-colour `routineCardPalette` can be derived. This
// is the foundation for both user-built custom themes (1–3 colours +
// mood) and AI-generated themes — see tasks/v2-themes.md §D2.
//
// Presets continue to set `tokens` directly; the recipe path is opt-in.

export interface ThemeRecipe {
  base: string; // primary colour (any CSS hex: #rgb, #rrggbb)
  secondary?: string;
  tertiary?: string;
  // Tunes contrast/saturation. Default: 'bright'.
  // - 'bright': high-saturation pastels, light/translucent surfaces
  // - 'soft':   warm low-contrast feel, light surfaces, no glow
  // - 'dark':   deep background, glowy shadow, dark translucent cards
  mood?: ThemeMood;
}

export type ThemeMood = 'bright' | 'soft' | 'dark';

interface Hsl {
  h: number; // 0..360
  s: number; // 0..1
  l: number; // 0..1
}

interface Rgb {
  r: number; // 0..255
  g: number; // 0..255
  b: number; // 0..255
}

// ---------- colour utilities ----------

function parseHex(hex: string): Rgb {
  const trimmed = hex.trim().replace(/^#/, '');
  const expanded =
    trimmed.length === 3
      ? trimmed
          .split('')
          .map((c) => c + c)
          .join('')
      : trimmed;
  if (!/^[0-9a-fA-F]{6}$/.test(expanded)) {
    throw new Error(`recipe: invalid hex colour "${hex}"`);
  }
  return {
    r: parseInt(expanded.slice(0, 2), 16),
    g: parseInt(expanded.slice(2, 4), 16),
    b: parseInt(expanded.slice(4, 6), 16),
  };
}

function rgbToHsl({ r, g, b }: Rgb): Hsl {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  switch (max) {
    case rn:
      h = ((gn - bn) / d + (gn < bn ? 6 : 0)) * 60;
      break;
    case gn:
      h = ((bn - rn) / d + 2) * 60;
      break;
    default:
      h = ((rn - gn) / d + 4) * 60;
  }
  return { h, s, l };
}

function hslToHex({ h, s, l }: Hsl): string {
  const hh = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((hh / 60) % 2) - 1));
  const m = l - c / 2;
  let r1 = 0;
  let g1 = 0;
  let b1 = 0;
  if (hh < 60) [r1, g1, b1] = [c, x, 0];
  else if (hh < 120) [r1, g1, b1] = [x, c, 0];
  else if (hh < 180) [r1, g1, b1] = [0, c, x];
  else if (hh < 240) [r1, g1, b1] = [0, x, c];
  else if (hh < 300) [r1, g1, b1] = [x, 0, c];
  else [r1, g1, b1] = [c, 0, x];
  const to255 = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${to255(r1)}${to255(g1)}${to255(b1)}`;
}

// WCAG relative luminance — used to pick a readable heading colour.
function relativeLuminance({ r, g, b }: Rgb): number {
  const lin = (c: number) => {
    const cs = c / 255;
    return cs <= 0.03928 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

function lighten(hex: string, amount: number): string {
  const hsl = rgbToHsl(parseHex(hex));
  // Clamp both ends — `amount` can be negative for the dark-mood
  // single-colour synthesised stop, which on a dark base would push
  // lightness below zero and `hslToHex` would emit invalid hex.
  const l = Math.max(0, Math.min(1, hsl.l + amount));
  return hslToHex({ ...hsl, l });
}

// ---------- public derivation ----------

const BRIGHT_PALETTE: { s: number; l: number } = { s: 0.7, l: 0.72 };
const SOFT_PALETTE: { s: number; l: number } = { s: 0.55, l: 0.78 };
const DARK_PALETTE: { s: number; l: number } = { s: 0.7, l: 0.6 };

function paletteParams(mood: ThemeMood): { s: number; l: number } {
  if (mood === 'soft') return SOFT_PALETTE;
  if (mood === 'dark') return DARK_PALETTE;
  return BRIGHT_PALETTE;
}

// Six hues spread evenly around the colour wheel, anchored at the base
// hue. Saturation/lightness are mood-tuned so the cards stay legible
// over the body background.
export function derivePalette(recipe: ThemeRecipe): string[] {
  const baseHsl = rgbToHsl(parseHex(recipe.base));
  const { s, l } = paletteParams(recipe.mood ?? 'bright');
  return [0, 60, 120, 180, 240, 300].map((offset) => hslToHex({ h: baseHsl.h + offset, s, l }));
}

export function deriveTokens(recipe: ThemeRecipe): ThemeTokens {
  const mood = recipe.mood ?? 'bright';
  const baseRgb = parseHex(recipe.base);
  const baseLum = relativeLuminance(baseRgb);

  const bodyBackground = deriveBodyBackground(recipe, mood);

  // Heading colour: dark on light backgrounds, light on dark ones. The
  // threshold is biased a bit because gradients dilute the perceived
  // luminance toward the lighter stop.
  const headingIsLight = mood === 'dark' || baseLum < 0.45;
  const pageHeadingColor = headingIsLight ? '#ffffff' : '#1e293b';

  const pageHeadingShadow = deriveHeadingShadow(recipe, mood, headingIsLight);

  // Surfaces: tighter alphas on dark moods (cards float over the dark
  // bg), looser on light moods (cards melt into the warm gradient).
  const routineCardSurface =
    mood === 'dark' ? 'rgba(255, 255, 255, 0.18)' : 'rgba(255, 255, 255, 0.55)';
  const cardSurface = mood === 'dark' ? 'rgba(255, 255, 255, 0.92)' : 'rgba(255, 255, 255, 0.75)';

  const accent = recipe.secondary ?? recipe.base;

  return {
    bodyBackground,
    pageHeadingColor,
    pageHeadingShadow,
    routineCardSurface,
    cardSurface,
    accent,
  };
}

function deriveBodyBackground(recipe: ThemeRecipe, mood: ThemeMood): string {
  const stops = [recipe.base, recipe.secondary, recipe.tertiary].filter(
    (s): s is string => typeof s === 'string',
  );
  // Single-colour recipe: synthesise a second stop so we still get a
  // gradient (flat fills look cheap).
  if (stops.length === 1) {
    const lightened = lighten(stops[0]!, mood === 'dark' ? -0.2 : 0.25);
    stops.push(lightened);
  }
  if (mood === 'dark') {
    // Radial reads as "deep night sky" regardless of hue — that's the
    // space-theme silhouette and it's what the dark mood is for.
    return `radial-gradient(ellipse at top, ${stops[0]} 0%, ${stops[stops.length - 1]} 100%)`;
  }
  if (stops.length === 2) {
    return `linear-gradient(180deg, ${stops[0]} 0%, ${stops[1]} 100%)`;
  }
  return `linear-gradient(180deg, ${stops[0]} 0%, ${stops[1]} 55%, ${stops[2]} 100%)`;
}

function deriveHeadingShadow(
  recipe: ThemeRecipe,
  mood: ThemeMood,
  headingIsLight: boolean,
): string {
  if (mood === 'dark') {
    // Glow keyed off accent so the shadow visually ties to the theme.
    const accent = recipe.secondary ?? recipe.base;
    return `0 0 18px ${hexWithAlpha(accent, 0.7)}`;
  }
  if (headingIsLight) {
    // Drop-shadow on light heading over a mid-tone bg.
    return '0 4px 0 rgba(0, 0, 0, 0.18)';
  }
  // Dark heading on light bg — a soft white halo lifts it off the page.
  return '0 3px 0 rgba(255, 255, 255, 0.55)';
}

function hexWithAlpha(hex: string, alpha: number): string {
  const { r, g, b } = parseHex(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
